import React, { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { Button, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { formatDateTimeRangeToISO } from '../../../utils/dateTimeUtils';
import TablePaginationBar from '../../../components/TablePaginationBar';
import SupportLoginFilter, { SupportLoginFilterOptions, DEFAULT_FILTER_OPTIONS } from './components/SupportLoginFilter';
import CreateShortCodeModal from './components/CreateShortCodeModal';
import {
    GET_SUPPORT_LOGIN_SHORT_CODE_GENERATION_LOG_PAGE,
    GetSupportLoginShortCodeGenerationLogPageResponse,
    GetSupportLoginShortCodeGenerationLogPageVariables,
    SupportLoginShortCodeGenerationLogType,
} from './graphql/queries/getSupportLoginShortCodeGenerationLogPage';

const SupportLoginPage: React.FC = () => {
    const { t } = useTranslation();
    const [tempFilterOptions, setTempFilterOptions] = useState<SupportLoginFilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [appliedFilters, setAppliedFilters] = useState<SupportLoginFilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [modalOpen, setModalOpen] = useState(false);

    const buildFilterVariables = useCallback((filters: SupportLoginFilterOptions) => {
        const filter: Record<string, unknown> = {};

        if (filters.operatorAccountId) {
            filter.operatorAccountId = parseInt(filters.operatorAccountId, 10) || undefined;
        }
        if (filters.operatorPhone) {
            filter.operatorPhone = parseInt(filters.operatorPhone, 10) || undefined;
        }
        if (filters.targetAccountId) {
            filter.targetAccountId = parseInt(filters.targetAccountId, 10) || undefined;
        }
        if (filters.targetPhone) {
            filter.targetPhone = parseInt(filters.targetPhone, 10) || undefined;
        }
        const timeRange = formatDateTimeRangeToISO(filters.createdAtRange);
        if (timeRange?.start) {
            filter.createdAtStart = dayjs(timeRange.start).unix();
        }
        if (timeRange?.end) {
            filter.createdAtEnd = dayjs(timeRange.end).unix();
        }

        return Object.keys(filter).length > 0 ? filter : undefined;
    }, []);

    const { data, loading, refetch } = useQuery<
        GetSupportLoginShortCodeGenerationLogPageResponse,
        GetSupportLoginShortCodeGenerationLogPageVariables
    >(GET_SUPPORT_LOGIN_SHORT_CODE_GENERATION_LOG_PAGE, {
        variables: {
            filter: buildFilterVariables(appliedFilters),
            pageRequest: { page: currentPage, size: pageSize },
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const pageData = data?.getSupportLoginShortCodeGenerationLogPage;
    const tableData = pageData?.content || [];
    const totalElements = pageData?.totalElements || 0;

    const handleFilter = () => {
        setAppliedFilters(tempFilterOptions);
        setCurrentPage(1);
        void refetch({
            filter: buildFilterVariables(tempFilterOptions),
            pageRequest: { page: 1, size: pageSize },
        });
    };

    const handleReset = () => {
        setTempFilterOptions(DEFAULT_FILTER_OPTIONS);
        setAppliedFilters(DEFAULT_FILTER_OPTIONS);
        setCurrentPage(1);
    };

    const formatTimestamp = (timestamp: number | null) => {
        if (!timestamp) return '-';
        return dayjs.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
    };

    const columns: TableColumnsType<SupportLoginShortCodeGenerationLogType> = [
        {
            title: t('supportTools.supportLogin.columns.id'),
            dataIndex: 'id',
            key: 'id',
            align: 'left' as const,
        },
        {
            title: t('supportTools.supportLogin.columns.operatorAccountId'),
            dataIndex: 'operatorAccountId',
            key: 'operatorAccountId',
            align: 'left' as const,
        },
        {
            title: t('supportTools.supportLogin.columns.operatorAccountName'),
            dataIndex: 'operatorAccountName',
            key: 'operatorAccountName',
            align: 'left' as const,
            render: (text: string | null) => text || '-',
        },
        {
            title: t('supportTools.supportLogin.columns.operatorPhone'),
            dataIndex: 'operatorPhone',
            key: 'operatorPhone',
            align: 'left' as const,
            render: (text: number | null) => text ?? '-',
        },
        {
            title: t('supportTools.supportLogin.columns.operatorAppRole'),
            dataIndex: 'operatorAppRole',
            key: 'operatorAppRole',
            align: 'left' as const,
        },
        {
            title: t('supportTools.supportLogin.columns.targetAccountId'),
            dataIndex: 'targetAccountId',
            key: 'targetAccountId',
            align: 'left' as const,
        },
        {
            title: t('supportTools.supportLogin.columns.targetAccountName'),
            dataIndex: 'targetAccountName',
            key: 'targetAccountName',
            align: 'left' as const,
            render: (text: string | null) => text || '-',
        },
        {
            title: t('supportTools.supportLogin.columns.targetPhone'),
            dataIndex: 'targetPhone',
            key: 'targetPhone',
            align: 'left' as const,
            render: (text: number | null) => text ?? '-',
        },
        {
            title: t('supportTools.supportLogin.columns.targetAccountRole'),
            dataIndex: 'targetAccountRole',
            key: 'targetAccountRole',
            align: 'left' as const,
        },
        {
            title: t('supportTools.supportLogin.columns.shortCode'),
            dataIndex: 'shortCode',
            key: 'shortCode',
            align: 'left' as const,
        },
        {
            title: t('supportTools.supportLogin.columns.isUsed'),
            dataIndex: 'isUsed',
            key: 'isUsed',
            align: 'left' as const,
            render: (isUsed: boolean) => isUsed ? t('common.yes') : t('common.no'),
        },
        {
            title: t('supportTools.supportLogin.columns.usedAt'),
            dataIndex: 'usedAt',
            key: 'usedAt',
            align: 'left' as const,
            render: (timestamp: number | null) => formatTimestamp(timestamp),
        },
        {
            title: t('supportTools.supportLogin.columns.createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'left' as const,
            render: (timestamp: number | null) => formatTimestamp(timestamp),
        },
    ];

    return (
        <div className="flex flex-col h-full">
            <SupportLoginFilter filters={tempFilterOptions} setFilters={setTempFilterOptions} />

            <div className="flex justify-between items-center mb-3 pl-0">
                <div className="flex gap-4">
                    <Button type="primary" onClick={() => setModalOpen(true)}>
                        <span className="font-bold">{t('supportTools.supportLogin.buttons.generateShortCode')}</span>
                    </Button>
                </div>
                <div className="flex gap-4">
                    <Button type="primary" onClick={handleFilter}>
                        <span className="font-bold">{t('common.search')}</span>
                    </Button>
                    <Button onClick={handleReset}>
                        <span className="font-bold">{t('common.reset')}</span>
                    </Button>
                </div>
            </div>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={tableData}
                loading={loading}
                pagination={false}
                tableLayout="auto"
                scroll={{ x: 'max-content' }}
                size="middle"
                className="mb-4"
            />

            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                }}
            />

            <CreateShortCodeModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={() => refetch()}
            />
        </div>
    );
};

export default SupportLoginPage;
